/**
 * LayoutNameModal — modal ustawień menu w Kreatorze menu (ADR-0031 Etap B, rozszerzony).
 *
 * Trzy tryby (różnią się tylko tytułem + wartościami początkowymi):
 *   - create — pusty formularz, tytuł „Nowe menu"
 *   - duplicate — prefilled `{currentName} (kopia)`, ikona/kolor źródła
 *   - rename — bieżące wartości edytowanego menu (ołówek na liście), tytuł „Edytuj menu"
 *
 * Pola: nazwa, ikona (pełna biblioteka lucide, picker w portalu), kolor (ZAMKNIĘTA paleta, żeby
 * pasek boczny zostawał spójny), przełącznik „Menu aktywne" (nieaktywne znika z paska bocznego).
 * Podgląd = okrągły kafelek dokładnie tak, jak menu wygląda w pasku.
 *
 * onSubmit dostaje znormalizowane wartości. Walidacja inline:
 *   - empty po trim → błąd `nameRequired`
 *   - > 120 chars → błąd `nameTooLong`
 *   - 409 NAME_TAKEN z backendu → caller ustawia `serverError` prop.
 */

import { type KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MenuSquare } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { IconPicker } from '@/components/modules/IconPicker';
import { resolveLucideIcon } from '@/lib/lucide-all-icons';
import { MENU_COLORS, type MenuColor, menuColorCss } from '@/lib/menu-colors';

export type LayoutNameModalMode = 'create' | 'duplicate' | 'rename';

/** Wartości formularza menu zwracane przy zapisie. */
export interface LayoutSettingsValues {
  name: string;
  /** Nazwa ikony lucide; null = ikona domyślna. */
  icon: string | null;
  /** Klucz koloru z palety; null = kolor domyślny (wg kolejności w pasku). */
  color: MenuColor | null;
  isActive: boolean;
}

export interface LayoutNameModalProps {
  open: boolean;
  mode: LayoutNameModalMode;
  /** Bieżąca nazwa edytowanego layoutu (dla duplicate/rename). */
  currentName?: string;
  /** Bieżąca ikona menu (dla duplicate/rename); brak = ikona domyślna. */
  currentIcon?: string | null;
  /** Bieżący kolor menu (dla duplicate/rename). */
  currentColor?: MenuColor | null;
  /** Bieżąca aktywność menu (dla rename); tworzone / kopiowane menu startuje jako aktywne. */
  currentActive?: boolean;
  /** Menu domyślne nie może być nieaktywne — przełącznik zablokowany z wyjaśnieniem. */
  activeLocked?: boolean;
  /** Submit handler — dostaje znormalizowane wartości formularza. */
  onSubmit: (values: LayoutSettingsValues) => void;
  onClose: () => void;
  /** Server-side error (np. 409 NAME_TAKEN) — wyświetlany inline pod inputem. */
  serverError?: string | null;
  /** Disable submit gdy mutacja in-flight. */
  submitting?: boolean;
  /**
   * i18n key prefix (np. 'tenant.menuLayoutBuilder' albo 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat).
   */
  i18nPrefix?: string;
}

export function LayoutNameModal({
  open,
  mode,
  currentName,
  currentIcon,
  currentColor,
  currentActive,
  activeLocked = false,
  onSubmit,
  onClose,
  serverError,
  submitting,
  i18nPrefix = 'tenant.menuLayoutBuilder',
}: LayoutNameModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [color, setColor] = useState<MenuColor | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [clientError, setClientError] = useState<string | null>(null);

  // Reset state przy każdym otwarciu (z prefill per mode).
  useEffect(() => {
    if (!open) return;
    if (mode === 'rename') {
      setName(currentName ?? '');
    } else if (mode === 'duplicate') {
      const suffix = t(`${i18nPrefix}.layouts.nameModal.copySuffix`);
      setName(currentName ? `${currentName} ${suffix}` : '');
    } else {
      setName('');
    }
    // Ikona/kolor: nowe menu startuje z domyślnymi, duplikat/edycja od wartości źródła.
    setIcon(mode === 'create' ? '' : (currentIcon ?? ''));
    setColor(mode === 'create' ? 'blue' : (currentColor ?? null));
    // Aktywność: tylko edycja zachowuje bieżący stan; nowe i kopie są aktywne.
    setIsActive(mode === 'rename' ? (currentActive ?? true) : true);
    setClientError(null);
  }, [open, mode, currentName, currentIcon, currentColor, currentActive, t, i18nPrefix]);

  const titleKey =
    mode === 'create'
      ? `${i18nPrefix}.layouts.nameModal.titleCreate`
      : mode === 'duplicate'
        ? `${i18nPrefix}.layouts.nameModal.titleDuplicate`
        : `${i18nPrefix}.layouts.nameModal.titleRename`;

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setClientError(t(`${i18nPrefix}.layouts.errors.nameRequired`));
      return;
    }
    if (trimmed.length > 120) {
      setClientError(t(`${i18nPrefix}.layouts.errors.nameTooLong`));
      return;
    }
    setClientError(null);
    onSubmit({ name: trimmed, icon: icon || null, color, isActive });
  };

  // Podgląd: ta sama ikona, którą pokaże pasek boczny (brak wyboru = ikona domyślna menu).
  const PreviewIcon = resolveLucideIcon(icon) ?? MenuSquare;

  const errorMsg = clientError ?? serverError ?? null;

  const toggleActive = () => {
    if (!activeLocked) setIsActive((v) => !v);
  };
  const onToggleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggleActive();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-border-secondary px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
            data-testid="layout-name-modal-cancel"
          >
            {t(`${i18nPrefix}.layouts.nameModal.cancel`)}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || name.trim().length === 0}
            className="flex items-center gap-1.5 rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
            data-testid="layout-name-modal-submit"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {t(`${i18nPrefix}.layouts.nameModal.submit`)}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label
            className="block text-sm font-medium text-text-primary"
            htmlFor="layout-name-input"
          >
            {t(`${i18nPrefix}.layouts.nameModal.label`)}
          </label>
          <input
            id="layout-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={t(`${i18nPrefix}.layouts.nameModal.placeholder`)}
            maxLength={120}
            autoFocus
            className="w-full rounded-md border border-border-secondary bg-bg-primary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none"
            data-testid="layout-name-modal-input"
          />
        </div>

        <div className="space-y-1.5" data-testid="layout-name-modal-icon">
          <span className="block text-sm font-medium text-text-primary">
            {t(`${i18nPrefix}.layouts.nameModal.iconLabel`)}
          </span>
          <div className="flex items-center gap-2">
            <IconPicker
              fullLibrary
              value={icon}
              onChange={setIcon}
              ariaLabel={t(`${i18nPrefix}.layouts.nameModal.iconLabel`)}
            />
            {icon ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIcon('')}
                data-testid="layout-name-modal-icon-reset"
              >
                {t(`${i18nPrefix}.layouts.nameModal.iconReset`)}
              </Button>
            ) : (
              <span className="text-xs text-text-muted">
                {t(`${i18nPrefix}.layouts.nameModal.iconDefault`)}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-1.5" data-testid="layout-name-modal-color">
          <span className="block text-sm font-medium text-text-primary">
            {t(`${i18nPrefix}.layouts.nameModal.colorLabel`)}
          </span>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {MENU_COLORS.map((c) => (
              <div
                key={c}
                role="radio"
                tabIndex={0}
                aria-checked={color === c}
                aria-label={t(`${i18nPrefix}.layouts.colors.${c}`)}
                title={t(`${i18nPrefix}.layouts.colors.${c}`)}
                onClick={() => setColor(c)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setColor(c);
                  }
                }}
                data-testid={`layout-name-modal-color-${c}`}
                className={[
                  'h-7 w-7 cursor-pointer rounded-full border-2 transition',
                  color === c
                    ? 'border-text-primary ring-2 ring-bg-primary ring-inset'
                    : 'border-transparent hover:scale-110',
                ].join(' ')}
                style={{ backgroundColor: menuColorCss(c) }}
              />
            ))}
          </div>
        </div>

        <div
          className="flex items-center gap-3 rounded-lg bg-bg-secondary px-3 py-2"
          data-testid="layout-name-modal-preview"
        >
          <div
            className={[
              'flex h-10 w-10 flex-none items-center justify-center rounded-full text-white',
              color ? '' : 'bg-gray-400',
            ].join(' ')}
            style={{
              ...(color ? { backgroundColor: menuColorCss(color) } : {}),
              opacity: isActive ? 1 : 0.35,
            }}
            data-testid="layout-name-modal-preview-tile"
          >
            <PreviewIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-text-primary">
              {t(`${i18nPrefix}.layouts.nameModal.previewLabel`)}
            </div>
            <div className="text-xs text-text-secondary">
              {isActive
                ? t(`${i18nPrefix}.layouts.nameModal.previewActive`)
                : t(`${i18nPrefix}.layouts.nameModal.previewInactive`)}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-text-primary">
              {t(`${i18nPrefix}.layouts.nameModal.activeLabel`)}
            </div>
            <div className="text-xs text-text-secondary">
              {activeLocked
                ? t(`${i18nPrefix}.layouts.nameModal.activeLockedHint`)
                : t(`${i18nPrefix}.layouts.nameModal.activeHint`)}
            </div>
          </div>
          <div
            role="switch"
            aria-checked={isActive}
            aria-disabled={activeLocked}
            aria-label={t(`${i18nPrefix}.layouts.nameModal.activeLabel`)}
            tabIndex={0}
            onClick={toggleActive}
            onKeyDown={onToggleKey}
            data-testid="layout-name-modal-active"
            className={[
              'relative h-5 w-9 flex-none cursor-pointer rounded-full transition-colors',
              isActive ? 'bg-accent-600' : 'bg-gray-300 dark:bg-gray-600',
              activeLocked ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
                isActive ? 'left-[18px]' : 'left-0.5',
              ].join(' ')}
            />
          </div>
        </div>

        {errorMsg ? (
          <p className="text-sm text-red-600" data-testid="layout-name-modal-error">
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
